import {
  Avatar,
  ModalActionButton,
  ModalContent,
  ModalContentActions,
  ModalContentBody,
  ModalContentFooter,
  ModalContentHeader,
  ModalOverlay,
  ModalPortal,
  TabsContentContainer,
  TabsList,
  TabsRootContainer,
} from '@socialchorus/shared-ui-components';
import { Link, Spinner } from '../../../ui';
import ViewTrigger from '../../../view-trigger';
import { ValueType } from '../../../../lib/utility-types';
import { useContentLikes } from '../../../../common/use-content-likes';
import { getContentAuthorProfilePath } from '../../../../models/content/helpers';
import { useTranslation } from 'react-i18next';
import { usePatronSelector } from '../../../../common/use-patron-selector';
import { programSelectors } from '../../../../models/program';
import { contentSelectors } from '../../../../models/content';
import { useMemo } from 'react';

type LikeListItemProps = {
  like: ValueType<ReturnType<typeof useContentLikes>['likes']>;
};
const LikeListItem = ({ like }: LikeListItemProps) => {
  return (
    <li>
      <Link
        href={getContentAuthorProfilePath(like.user_id)}
        action="push"
        className="view-likes-list__item--link"
      >
        <div className="view-likes-list__item">
          <Avatar
            imgSrc={like.avatar.url || undefined}
            shape="circle"
            size="medium"
          />
          <span className="view-likes-list__item--name">{like.name}</span>
          <span className="view-likes-list__item--description">
            {like.details}
          </span>
        </div>
      </Link>
    </li>
  );
};

type ModalProps = {
  contentId: number;
};

const ViewLikesModalBody = ({ contentId }: ModalProps) => {
  const { likes, canLoadMore, isLoadingMore, fetchNextPage } = useContentLikes({
    contentId,
  });

  return (
    <ModalContentBody>
      <ul className="view-likes-list">
        {likes.map((like) => (
          <LikeListItem key={like.user_id} like={like} />
        ))}
        {isLoadingMore ? (
          <Spinner />
        ) : canLoadMore ? (
          <ViewTrigger offset="50px" onInview={() => fetchNextPage()} />
        ) : null}
      </ul>
    </ModalContentBody>
  );
};

const ViewReactionsTabContainer = ({
  reactionId,
  contentId,
}: { reactionId: string } & ModalProps) => {
  const { likes, canLoadMore, isLoadingMore, fetchNextPage } = useContentLikes({
    contentId,
    reactionTypeId: reactionId,
  });
  return (
    <TabsContentContainer value={reactionId}>
      <ul className="view-likes-list">
        {likes.map((like) => (
          <LikeListItem key={like.user_id} like={like} />
        ))}
        {isLoadingMore ? (
          <Spinner />
        ) : canLoadMore ? (
          <ViewTrigger offset="50px" onInview={() => fetchNextPage()} />
        ) : null}
      </ul>
    </TabsContentContainer>
  );
};

const ViewReactionsModalBody = ({ contentId }: ModalProps) => {
  const { t } = useTranslation();
  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId })
  );

  const tabs = useMemo(() => {
    return reactions
      .filter((reaction) => reaction.count > 0)
      .sort((a, b) => b.count - a.count)
      .map((reaction) => ({
        value: reaction.id,
        label: `${reaction.emoji} ${reaction.count}`,
      }));
  }, [reactions]);

  return (
    <ModalContentBody className="view-reactions-container">
      {tabs.length === 0 ? (
        <div className="text-body-1">{t('content.reaction.empty')}</div>
      ) : (
        <TabsRootContainer defaultValue={tabs[0].value}>
          <TabsList tabs={tabs} />
          {tabs.map((tab) => (
            <ViewReactionsTabContainer
              key={tab.value}
              reactionId={tab.value}
              contentId={contentId}
            />
          ))}
        </TabsRootContainer>
      )}
    </ModalContentBody>
  );
};

export const ViewLikesModal = ({ contentId }: ModalProps) => {
  const { t } = useTranslation();
  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );
  return (
    <ModalPortal>
      <ModalOverlay />
      <ModalContent size="medium">
        <ModalContentHeader
          title={
            expandedReactionTypesEnabled
              ? t('content.reaction_list')
              : t('content.like_list')
          }
        />
        {expandedReactionTypesEnabled ? (
          <ViewReactionsModalBody contentId={contentId} />
        ) : (
          <ViewLikesModalBody contentId={contentId} />
        )}
        <ModalContentFooter>
          <ModalContentActions>
            <ModalActionButton label={t('common.close')} />
          </ModalContentActions>
        </ModalContentFooter>
      </ModalContent>
    </ModalPortal>
  );
};
